import React, { useState, useRef, useEffect, useMemo } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Plus, Upload, File, X, PencilSimple, UserPlus, ArrowClockwise, CurrencyEur, CheckCircle } from '@phosphor-icons/react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useOrderForm } from './useOrderForm';
import OrderFormFields from './OrderFormFields';
import RefabSearchBlock, { computeRefabChanges, getChangedFieldKeys } from './RefabSearchBlock';

export default function ManualOrderDialog({ open, onOpenChange, onSuccess, editOrder = null }) {
  const fileInputLeft = useRef(null);
  const fileInputRight = useRef(null);

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [clientOpen, setClientOpen] = useState(false);
  const [receptionMode, setReceptionMode] = useState('stl');
  const [stlLeft, setStlLeft] = useState(null);
  const [stlRight, setStlRight] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // New client inline creation
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', first_name: '', email: '', company_id: '', phone: '', password: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  // Companies for autocomplete
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyOpen, setCompanyOpen] = useState(false);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', address: '', email: '', phone: '' });
  const [creatingCompany, setCreatingCompany] = useState(false);

  // Refabrication state
  const [refabOriginal, setRefabOriginal] = useState(null);

  // Establishment selection for order
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');

  const form = useOrderForm(products);
  const isEditMode = !!editOrder;

  useEffect(() => {
    if (open) {
      fetchClients();
      fetchProducts();
      fetchCompanies();
      if (!editOrder) {
        form.reset();
        setClientId('');
        setReceptionMode('stl');
        setStlLeft(null);
        setStlRight(null);
        setShowNewClient(false);
        setNewClient({ name: '', first_name: '', email: '', company_id: '', phone: '', password: '' });
        setShowNewCompany(false);
        setNewCompany({ name: '', address: '', email: '', phone: '' });
        setRefabOriginal(null);
      }
    }
  }, [open]);

  useEffect(() => {
    if (editOrder && products.length > 0) {
      form.loadFromOrder(editOrder);
      setClientId(editOrder.client_id || '');
      setReceptionMode(editOrder.options?.reception_mode || 'stl');
    }
  }, [editOrder, products]);

  const fetchClients = async () => {
    try {
      const r = await axios.get(`${API}/clients`);
      setClients(r.data);
    } catch (e) { console.error('Fetch clients error:', e.message); }
  };

  const fetchCompanies = async () => {
    try {
      const r = await axios.get(`${API}/companies`);
      setCompanies(r.data);
    } catch (e) { console.error('Fetch companies error:', e.message); }
  };

  // Fetch establishments when client changes
  useEffect(() => {
    if (clientId) {
      const fetchEstablishments = async () => {
        try {
          const r = await axios.get(`${API}/users/${clientId}/establishments`);
          setEstablishments(r.data);
          if (r.data.length === 1) {
            setSelectedEstablishmentId(r.data[0].id);
          } else {
            setSelectedEstablishmentId('');
          }
        } catch (e) { console.error('Fetch establishments error:', e.message); setEstablishments([]); }
      };
      fetchEstablishments();
    } else {
      setEstablishments([]);
      setSelectedEstablishmentId('');
    }
  }, [clientId]);

  const handleCreateCompany = async () => {
    if (!newCompany.name.trim()) { toast.error("Le nom de l'entreprise est obligatoire"); return; }
    setCreatingCompany(true);
    try {
      const r = await axios.post(`${API}/companies`, newCompany);
      toast.success(`Société "${r.data.name}" créée`);
      // Auto-create a default establishment for the new company
      const estR = await axios.post(`${API}/establishments`, {
        company_id: r.data.id,
        name: newCompany.name.trim(),
        address: newCompany.address || '',
        email: newCompany.email || '',
        phone: newCompany.phone || '',
      });
      setCompanies(prev => [...prev, r.data]);
      setNewClient(p => ({ ...p, company_id: r.data.id }));
      setCompanySearch(r.data.name);
      setShowNewCompany(false);
      setNewCompany({ name: '', address: '', email: '', phone: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création entreprise");
    } finally { setCreatingCompany(false); }
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim() || !newClient.email.trim()) {
      toast.error('Nom et email sont obligatoires');
      return;
    }
    if (!newClient.company_id) {
      toast.error("L'entreprise est obligatoire");
      return;
    }
    const selectedCompany = companies.find(c => c.id === newClient.company_id);
    setCreatingClient(true);
    try {
      // Fetch establishments for the selected company to auto-associate
      let estIds = [];
      try {
        const estR = await axios.get(`${API}/establishments?company_id=${newClient.company_id}`);
        estIds = estR.data.map(e => e.id);
      } catch (e) { console.error('Failed to fetch establishments:', e); }

      const r = await axios.post(`${API}/clients`, {
        name: [newClient.name, newClient.first_name].filter(Boolean).join(' '),
        email: newClient.email,
        company_id: newClient.company_id,
        company_name: selectedCompany?.name || '',
        phone: newClient.phone,
        password: newClient.password || undefined,
        establishment_ids: estIds,
      });
      toast.success(`Client "${r.data.name}" créé`);
      setClients(prev => [...prev, r.data]);
      setClientId(r.data.id);
      setShowNewClient(false);
      setNewClient({ name: '', first_name: '', email: '', company_id: '', phone: '', password: '' });
      setCompanySearch('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création client');
    } finally {
      setCreatingClient(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const r = await axios.get(`${API}/products`);
      setProducts(r.data);
    } catch (e) { console.error('Failed to fetch products:', e); }
  };

  const handleFileSelect = (side, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.stl')) { toast.error('Veuillez sélectionner un fichier STL'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Le fichier ne doit pas dépasser 15 Mo'); return; }
    if (side === 'left') setStlLeft(file); else setStlRight(file);
    toast.success(`Fichier ${side === 'left' ? 'OG' : 'OD'} sélectionné`);
  };

  const uploadSTLFile = async (orderId, file, side) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/orders/${orderId}/upload-stl?side=${side}`, formData);
      return true;
    } catch (e) { return false; }
  };

  // Refabrication: select original order and prefill
  const handleSelectRefabOrder = (order) => {
    setRefabOriginal(order);
    setClientId(order.client_id);
    form.loadFromOrder(order);
    toast.success('Formulaire prérempli depuis la commande d\'origine');
  };

  const handleClearRefab = () => {
    setRefabOriginal(null);
    form.reset();
    setClientId('');
  };

  // Compute refab changes in real-time
  const refabChanges = useMemo(() => {
    if (!refabOriginal) return [];
    const currentOpts = form.buildFormData().options || {};
    return computeRefabChanges(refabOriginal, currentOpts, form.matiere, form.couleur);
  }, [refabOriginal, form.formeEmbout, form.matiere, form.durete, form.couleur, form.etancheite, form.finition,
      form.emboutType, form.marque, form.modele, form.ecouteurVersion, form.ecouteurPuissance,
      form.ecouteurType, form.ecouteurPuissanceOticon, form.eventOD, form.eventOG]);

  const changedFields = useMemo(() => getChangedFieldKeys(refabChanges), [refabChanges]);
  const isRefabModified = refabChanges.length > 0;
  const isRefabFree = refabOriginal && !isRefabModified && refabOriginal.refab_eligible_free;
  const formeChanged = changedFields.has('forme_embout');

  // Serial number logic for refab
  const getRefabSerials = () => {
    if (!refabOriginal) return { left: null, right: null };
    const origLeft = refabOriginal.serial_number_left;
    const origRight = refabOriginal.serial_number_right;
    if (formeChanged) {
      return {
        left: origLeft ? (origLeft.startsWith('R') ? origLeft : `R${origLeft}`) : null,
        right: origRight ? (origRight.startsWith('R') ? origRight : `R${origRight}`) : null,
      };
    }
    return { left: origLeft, right: origRight };
  };

  const handleSubmit = async () => {
    if (!clientId) { toast.error('Sélectionnez un client'); return; }
    if (!form.patientRef.trim()) { toast.error('Veuillez entrer une référence patient'); return; }
    if (!form.earSide) { toast.error('Veuillez sélectionner un côté'); return; }
    if (!form.resolvedProductKey || !form.resolvedProduct) {
      if (form.fabricationType === 'obturateur' && !form.obturateurType) toast.error("Veuillez sélectionner un type d'obturateur");
      else if (form.fabricationType === 'embout_auditif' && !form.formeEmbout) toast.error("Veuillez sélectionner une forme d'embout");
      else toast.error('Produit introuvable. Vérifiez vos choix.');
      return;
    }
    // Conformateur d'oreille : description obligatoire (min 10 caractères)
    if (form.fabricationType === 'conformateur_oreille') {
      const desc = (form.conformateurDescription || '').trim();
      if (desc.length < 10) {
        toast.error("Merci de décrire le besoin du conformateur d'oreille (10 caractères minimum).");
        return;
      }
    }

    setIsLoading(true);
    try {
      const formData = form.buildFormData();
      formData.options.envoi_courrier = receptionMode === 'courrier';
      formData.options.reception_email = receptionMode === 'email';
      formData.options.reception_mode = receptionMode;

      let order;
      if (isEditMode) {
        // Préserver la demande de validation 3D du client lors d'une édition technicien
        formData.options.wants_3d_validation = editOrder.wants_3d_validation || editOrder.options?.wants_3d_validation || false;
        formData.options.validation_email = editOrder.validation_email || editOrder.options?.validation_email || null;
        const response = await axios.put(`${API}/orders/${editOrder.id}`, formData);
        order = response.data;
        toast.success('Commande modifiée avec succès');
      } else {
        formData.client_id = clientId;
        formData.establishment_id = selectedEstablishmentId || null;

        // Refabrication data
        if (refabOriginal) {
          formData.is_refabrication = true;
          formData.original_serial_number = refabOriginal.serial_number_left || refabOriginal.serial_number_right;
          formData.refab_origin_order_id = refabOriginal.id;
          formData.refab_is_modified = isRefabModified;
          formData.refab_changes = refabChanges;
          const serials = getRefabSerials();
          formData.refab_serial_left_override = serials.left;
          formData.refab_serial_right_override = serials.right;
        } else {
          formData.is_refabrication = false;
          formData.original_serial_number = null;
        }

        const response = await axios.post(`${API}/orders/manual-entry`, formData);
        order = response.data;

        if (receptionMode === 'stl') {
          const uploads = [];
          if (stlLeft && (form.earSide === 'left' || form.earSide === 'both')) uploads.push(uploadSTLFile(order.id, stlLeft, 'left'));
          if (stlRight && (form.earSide === 'right' || form.earSide === 'both')) uploads.push(uploadSTLFile(order.id, stlRight, 'right'));
          if (uploads.length > 0) {
            const results = await Promise.all(uploads);
            if (results.some(r => !r)) toast.warning("Commande créée mais certains fichiers n'ont pas pu être uploadés");
          }
        }

        if (refabOriginal) {
          toast.success(`Refabrication ${isRefabModified ? 'modifiée' : 'identique'} créée — ${isRefabFree ? 'GRATUITE' : 'PAYANTE'}`);
        } else {
          toast.success('Commande créée avec succès');
        }
      }

      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.id === clientId);
  const refabSerials = getRefabSerials();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode
              ? <><PencilSimple size={24} className="text-blue-600" />Modifier la commande</>
              : refabOriginal
                ? <><ArrowClockwise size={24} className="text-amber-600" />Saisir une refabrication</>
                : <><Plus size={24} className="text-zinc-900" />Saisir une commande</>
            }
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? `Modification de la commande pour ${editOrder?.patient_reference}` : refabOriginal ? `Refabrication à partir de ${refabOriginal.patient_reference}` : 'Créer une commande manuellement (commande postale ou téléphonique)'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-5 py-2">
          {/* Refabrication Block - always visible at top when not editing */}
          {!isEditMode && (
            <RefabSearchBlock
              onSelectOrder={handleSelectRefabOrder}
              onClear={handleClearRefab}
              selectedOriginal={refabOriginal}
            />
          )}

          {/* Refab Status Badge */}
          {refabOriginal && (
            <div className={`flex items-center justify-between p-3 rounded-sm border-2 ${isRefabModified ? 'bg-red-50 border-red-300' : isRefabFree ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="refab-status-badge">
              <div className="flex items-center gap-3">
                {isRefabModified ? (
                  <>
                    <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase">Modifiée</span>
                    <span className="bg-red-100 text-red-800 text-xs font-bold px-3 py-1 rounded-sm uppercase">Payante</span>
                  </>
                ) : isRefabFree ? (
                  <>
                    <span className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase">Identique</span>
                    <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-sm uppercase">Gratuite</span>
                  </>
                ) : (
                  <>
                    <span className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase">Identique</span>
                    <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-sm uppercase">Payante (&gt;365j)</span>
                  </>
                )}
              </div>
              {refabSerials.left || refabSerials.right ? (
                <div className="text-xs text-zinc-600">
                  N° série: {refabSerials.left && <span className={formeChanged ? 'text-red-600 font-bold' : ''}>{refabSerials.left}</span>}
                  {refabSerials.left && refabSerials.right && ' | '}
                  {refabSerials.right && <span className={formeChanged ? 'text-red-600 font-bold' : ''}>{refabSerials.right}</span>}
                </div>
              ) : null}
            </div>
          )}

          {/* Changes list */}
          {refabOriginal && refabChanges.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3" data-testid="refab-changes-list">
              <p className="text-xs font-bold text-red-700 uppercase mb-2">Modifications détectées ({refabChanges.length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {refabChanges.map(c => (
                  <div key={c.field} className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-red-800">{c.label}:</span>
                    <span className="text-zinc-500 line-through">{c.old || '—'}</span>
                    <span className="text-red-700">→</span>
                    <span className="font-bold text-red-800">{c.new || '—'}</span>
                  </div>
                ))}
              </div>
              {formeChanged && (
                <p className="text-xs text-red-700 mt-2 font-medium">La forme a été modifiée → N° de série préfixé par "R"</p>
              )}
            </div>
          )}

          {/* Client Selection */}
          {!refabOriginal && (
            <div>
              <Label className="label-overline mb-2 block">Client *</Label>
              {showNewClient ? (
                <div className="border border-zinc-200 rounded-sm p-3 sm:p-4 space-y-4 bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-semibold text-sm">Nouveau client</span>
                    <Button variant="ghost" size="sm" onClick={() => { setShowNewClient(false); setShowNewCompany(false); }} className="text-xs">Annuler</Button>
                  </div>

                  {/* Client fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-500">Nom *</Label>
                      <Input placeholder="Dupont" value={newClient.name} onChange={e => setNewClient(p => ({...p, name: e.target.value}))} className="rounded-sm mt-1" data-testid="new-client-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Prénom</Label>
                      <Input placeholder="Marie" value={newClient.first_name} onChange={e => setNewClient(p => ({...p, first_name: e.target.value}))} className="rounded-sm mt-1" data-testid="new-client-firstname" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Email *</Label>
                      <Input placeholder="email@exemple.com" type="email" value={newClient.email} onChange={e => setNewClient(p => ({...p, email: e.target.value}))} className="rounded-sm mt-1" data-testid="new-client-email" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Téléphone</Label>
                      <Input placeholder="06 12 34 56 78" value={newClient.phone} onChange={e => setNewClient(p => ({...p, phone: e.target.value}))} className="rounded-sm mt-1" data-testid="new-client-phone" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Mot de passe</Label>
                      <Input placeholder="Mot de passe" type="password" value={newClient.password} onChange={e => setNewClient(p => ({...p, password: e.target.value}))} className="rounded-sm mt-1" data-testid="new-client-password" />
                    </div>
                  </div>

                  {/* Company selection with autocomplete */}
                  <div className="border-t border-zinc-200 pt-3">
                    <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Entreprise *</Label>
                    {!showNewCompany ? (
                      <div className="mt-2">
                        <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="w-full justify-between rounded-sm bg-white text-left" data-testid="new-client-company-select">
                              {newClient.company_id
                                ? companies.find(c => c.id === newClient.company_id)?.name || 'Entreprise sélectionnée'
                                : 'Rechercher une entreprise...'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 bg-white w-[--radix-popover-trigger-width]" align="start">
                            <Command>
                              <CommandInput placeholder="Rechercher une entreprise..." className="h-9" data-testid="company-search-input" />
                              <CommandList>
                                <CommandEmpty>
                                  <div className="p-2 text-center">
                                    <p className="text-sm text-zinc-500 mb-2">Aucune entreprise trouvée</p>
                                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setShowNewCompany(true); setCompanyOpen(false); }} data-testid="create-company-from-search">
                                      <Plus size={14} className="mr-1" /> Créer une entreprise
                                    </Button>
                                  </div>
                                </CommandEmpty>
                                <CommandGroup>
                                  {companies.map(c => (
                                    <CommandItem key={c.id} value={`${c.name} ${c.address || ''}`} onSelect={() => { setNewClient(p => ({...p, company_id: c.id})); setCompanySearch(c.name); setCompanyOpen(false); }} data-testid={`company-option-${c.id}`}>
                                      <Check className={cn("mr-2 h-4 w-4", newClient.company_id === c.id ? "opacity-100" : "opacity-0")} />
                                      <div>
                                        <p className="font-medium text-sm">{c.name}</p>
                                        {c.address && <p className="text-xs text-zinc-400">{c.address}</p>}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <Button variant="ghost" size="sm" className="mt-1.5 text-xs text-zinc-500 hover:text-zinc-800" onClick={() => setShowNewCompany(true)} data-testid="create-company-btn">
                          <Plus size={12} className="mr-1" /> Créer une nouvelle entreprise
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-2 border border-dashed border-zinc-300 rounded-sm p-3 bg-white space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-zinc-600">Nouvelle entreprise</span>
                          <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setShowNewCompany(false)}>Annuler</Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input placeholder="Nom de l'entreprise *" value={newCompany.name} onChange={e => setNewCompany(p => ({...p, name: e.target.value}))} className="rounded-sm" data-testid="new-company-name" />
                          <Input placeholder="Adresse" value={newCompany.address} onChange={e => setNewCompany(p => ({...p, address: e.target.value}))} className="rounded-sm" data-testid="new-company-address" />
                          <Input placeholder="Email" type="email" value={newCompany.email} onChange={e => setNewCompany(p => ({...p, email: e.target.value}))} className="rounded-sm" data-testid="new-company-email" />
                          <Input placeholder="Téléphone" value={newCompany.phone} onChange={e => setNewCompany(p => ({...p, phone: e.target.value}))} className="rounded-sm" data-testid="new-company-phone" />
                        </div>
                        <Button onClick={handleCreateCompany} disabled={creatingCompany} className="bg-zinc-900 text-white rounded-sm" size="sm" data-testid="save-new-company-btn">
                          {creatingCompany ? 'Création...' : "Enregistrer l'entreprise"}
                        </Button>
                      </div>
                    )}
                  </div>

                  <Button onClick={handleCreateClient} disabled={creatingClient} className="bg-zinc-900 text-white rounded-sm w-full" size="sm" data-testid="save-new-client-btn">
                    <UserPlus size={14} className="mr-1" />{creatingClient ? 'Création...' : 'Créer le client'}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Popover open={clientOpen} onOpenChange={setClientOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="flex-1 justify-between rounded-sm bg-white" data-testid="manual-client-select">
                        {selectedClient ? `${selectedClient.name} — ${selectedClient.company_name || ''}` : 'Sélectionner un client...'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 bg-white" align="start">
                      <Command>
                        <CommandInput placeholder="Rechercher..." className="h-9" />
                        <CommandList>
                          <CommandEmpty>Aucun client trouvé</CommandEmpty>
                          <CommandGroup>
                            {clients.map(c => (
                              <CommandItem key={c.id} value={`${c.name} ${c.company_name || ''} ${c.email}`} onSelect={() => { setClientId(c.id); setClientOpen(false); }}>
                                <Check className={cn("mr-2 h-4 w-4", clientId === c.id ? "opacity-100" : "opacity-0")} />
                                <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-zinc-500">{c.company_name} — {c.email}</p></div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button variant="outline" size="icon" className="shrink-0 rounded-sm" onClick={() => setShowNewClient(true)} title="Nouveau client"><UserPlus size={18} /></Button>
                </div>
              )}
            </div>
          )}

          {/* When refab, show client info */}
          {refabOriginal && (
            <div className="flex items-center gap-2 text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
              <span className="font-medium">Client:</span> <span>{refabOriginal.client_name}</span>
              {refabOriginal.client_company && <span className="text-zinc-400">({refabOriginal.client_company})</span>}
            </div>
          )}

          {/* Establishment selector — shown when client has multiple establishments */}
          {clientId && establishments.length > 1 && (
            <div className="bg-white border border-zinc-200 p-3 rounded-sm" data-testid="establishment-selector">
              <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Établissement *</Label>
              <Select value={selectedEstablishmentId} onValueChange={setSelectedEstablishmentId}>
                <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="establishment-select">
                  <SelectValue placeholder="Sélectionner un établissement..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {establishments.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.address ? `— ${e.address}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {clientId && establishments.length === 1 && (
            <div className="flex items-center gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-sm px-3 py-2" data-testid="establishment-auto-selected">
              <Building2 size={14} className="text-zinc-400" />
              Établissement : <span className="font-medium text-zinc-700">{establishments[0].name}</span>
            </div>
          )}

          {/* Order Form Fields — pass changed fields for red highlighting */}
          <OrderFormFields form={form} compact changedFields={refabOriginal ? changedFields : null} />

          {/* STL Files */}
          {!isEditMode && (
            <div className="bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm">
              <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Réception empreintes</h3>
              <RadioGroup value={receptionMode} onValueChange={setReceptionMode} className="flex flex-wrap gap-3 sm:gap-4 mb-3">
                <div className="flex items-center space-x-2"><RadioGroupItem value="stl" id="stl" /><Label htmlFor="stl" className="cursor-pointer text-sm">Fichiers STL</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="courrier" id="courrier" /><Label htmlFor="courrier" className="cursor-pointer text-sm">Courrier</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="email" id="email" /><Label htmlFor="email" className="cursor-pointer text-sm">Email</Label></div>
              </RadioGroup>
              {receptionMode === 'stl' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(form.earSide === 'left' || form.earSide === 'both') && (
                    <div className={`border-2 border-dashed rounded-sm p-3 text-center cursor-pointer transition-colors ${stlLeft ? 'border-emerald-400 bg-emerald-50' : 'border-zinc-300 hover:border-zinc-400'}`} onClick={() => fileInputLeft.current?.click()}>
                      <input ref={fileInputLeft} type="file" accept=".stl" onChange={e => handleFileSelect('left', e.target.files[0])} className="hidden" />
                      {stlLeft ? <><File size={20} className="mx-auto text-emerald-600" /><p className="text-xs mt-1 text-emerald-700 font-medium">{stlLeft.name}</p><button onClick={(e) => { e.stopPropagation(); setStlLeft(null); }} className="text-xs text-red-500 mt-1">Supprimer</button></> : <><Upload size={20} className="mx-auto text-zinc-400" /><p className="text-xs mt-1 text-zinc-500">STL Oreille Gauche</p></>}
                    </div>
                  )}
                  {(form.earSide === 'right' || form.earSide === 'both') && (
                    <div className={`border-2 border-dashed rounded-sm p-3 text-center cursor-pointer transition-colors ${stlRight ? 'border-emerald-400 bg-emerald-50' : 'border-zinc-300 hover:border-zinc-400'}`} onClick={() => fileInputRight.current?.click()}>
                      <input ref={fileInputRight} type="file" accept=".stl" onChange={e => handleFileSelect('right', e.target.files[0])} className="hidden" />
                      {stlRight ? <><File size={20} className="mx-auto text-emerald-600" /><p className="text-xs mt-1 text-emerald-700 font-medium">{stlRight.name}</p><button onClick={(e) => { e.stopPropagation(); setStlRight(null); }} className="text-xs text-red-500 mt-1">Supprimer</button></> : <><Upload size={20} className="mx-auto text-zinc-400" /><p className="text-xs mt-1 text-zinc-500">STL Oreille Droite</p></>}
                    </div>
                  )}
                </div>
              )}
              {receptionMode === 'courrier' && <div className="bg-blue-50 p-3 rounded-sm border border-blue-200"><p className="text-sm text-blue-700">Empreintes reçues par courrier. Merci de bien indiquer la référence patient sur l'envoi.</p></div>}
              {receptionMode === 'email' && <div className="bg-blue-50 p-3 rounded-sm border border-blue-200"><p className="text-sm text-blue-700">Empreintes attendues par email. Le client sera informé de l'adresse d'envoi.</p></div>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={handleSubmit} disabled={isLoading} className={`rounded-sm text-white ${refabOriginal ? 'bg-amber-600 hover:bg-amber-700' : 'bg-zinc-900 hover:bg-zinc-800'}`} data-testid="submit-order-btn">
            {isLoading ? 'Envoi...' : refabOriginal ? (isRefabFree ? 'Créer refabrication (gratuite)' : 'Créer refabrication (payante)') : isEditMode ? 'Enregistrer' : 'Créer la commande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
